import React, { useState, useCallback } from 'react';
import { SQLQueryOperation } from '../../types/dataset.types';
import * as styles from './SQLQueryViewer.css.ts';
import logger from '../utils/logger';

interface SQLQueryViewerProps {
  operation: SQLQueryOperation;
  targetDatasetName?: string;
  onCopyToEditor?: () => void;
}

/**
 * SQLQueryViewer
 *
 * SQLクエリを読み取り専用で表示するコンポーネント
 * - シンタックスハイライト風の表示
 * - クリップボードへのコピー機能
 * - 新しいエディタで開く機能
 */
export const SQLQueryViewer: React.FC<SQLQueryViewerProps> = ({
  operation,
  targetDatasetName,
  onCopyToEditor
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // クエリをクリップボードにコピー
  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(operation.query);
      setCopySuccess(true);
      logger.info('[SQLQueryViewer] Query copied to clipboard');

      // 2秒後に成功表示をリセット
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      logger.error('[SQLQueryViewer] Failed to copy query:', error);
    }
  }, [operation.query]);

  // クエリの行数を計算
  const lineCount = operation.query.split('\n').length;
  const isMultiLine = lineCount > 1;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          SQL Query {targetDatasetName ? `(from ${targetDatasetName})` : ''}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.copyButton}
            onClick={handleCopyToClipboard}
            title="Copy query to clipboard"
          >
            {copySuccess ? '✓ Copied' : 'Copy'}
          </button>

          {onCopyToEditor && (
            <button
              className={styles.editButton}
              onClick={onCopyToEditor}
              title="Open in new SQL Editor"
            >
              Copy to New Editor
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.readOnlyNotice}>
          This query is read-only. Use "Copy to New Editor" to create a modified version.
        </div>

        <div className={styles.queryContainer}>
          <pre className={`${styles.query} ${isMultiLine ? styles.queryWithLineNumbers : ''}`}>
            <code>{operation.query}</code>
          </pre>

          {isMultiLine && (
            <div className={styles.lineNumbers}>
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i} className={styles.lineNumber}>
                  {i + 1}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.metadata}>
          <span className={styles.metadataItem}>
            Target Dataset ID: {operation.targetDatasetId}
          </span>
          {operation.description && (
            <span className={styles.metadataItem}>
              Description: {operation.description}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};